import { fromJson, toBase64, fromBase64, fromByteArray, toByteArray } from '@afz/base-library';
import { DEFAULT_CRIPTO_MODE, DEFAULT_CRIPTO_LENGTH, DEFAULT_CRIPTO_IV_LENGTH, DEFAULT_CRIPTO_KEY_ALG, DEFAULT_CRIPTO_KEY_HASH, DEFAULT_CRIPTO_KEY_SALT, DEFAULT_CRIPTO_KEY_ITERATIONS, } from './constants.js';
import { EmptySessionTokenError, InvalidSessionTokenError } from './error/mod.js';
const IMPORT_ALG = { name: DEFAULT_CRIPTO_KEY_ALG };
const IMPORT_KEY_USAGES = ['deriveKey'];
const DERIVE_KEY_USAGES = ['encrypt', 'decrypt'];
const TOKEN_FORMAT = /^([a-zA-Z0-9+/=]+)\.([a-zA-Z0-9+/=]+)$/;
async function createKey(cryptoKey, mode, length) {
    const alg = {
        name: DEFAULT_CRIPTO_KEY_ALG,
        hash: DEFAULT_CRIPTO_KEY_HASH,
        salt: new TextEncoder().encode(DEFAULT_CRIPTO_KEY_SALT),
        iterations: DEFAULT_CRIPTO_KEY_ITERATIONS,
    };
    const derived = { name: mode, length: length };
    const encoded = new TextEncoder().encode(cryptoKey);
    const key = await crypto.subtle.importKey('raw', encoded, IMPORT_ALG, false, IMPORT_KEY_USAGES);
    return crypto.subtle.deriveKey(alg, key, derived, false, DERIVE_KEY_USAGES);
}
export async function encrypt(secret, cryptoKey, mode = DEFAULT_CRIPTO_MODE, length = DEFAULT_CRIPTO_LENGTH, ivLength = DEFAULT_CRIPTO_IV_LENGTH) {
    const alg = {
        name: mode,
        length,
        iv: crypto.getRandomValues(new Uint8Array(ivLength)),
    };
    const encoded = new TextEncoder().encode(secret);
    const key = await createKey(cryptoKey, mode, length);
    return {
        ciphered: await crypto.subtle.encrypt(alg, key, encoded),
        vector: alg.iv,
    };
}
export async function decrypt(data, cryptoKey, mode = DEFAULT_CRIPTO_MODE, length = DEFAULT_CRIPTO_LENGTH) {
    const alg = {
        name: mode,
        length,
        iv: data.vector,
    };
    const key = await createKey(cryptoKey, mode, length);
    const decrypted = await crypto.subtle.decrypt(alg, key, data.ciphered);
    return new TextDecoder().decode(decrypted);
}
export async function createSessionToken(userName, passphrase, cryptoKey) {
    const [eUserName, ePassphrase] = await Promise.all([encrypt(userName, cryptoKey), encrypt(passphrase, cryptoKey)]);
    const dUserName = {
        ciphered: fromByteArray(eUserName.ciphered),
        vector: fromByteArray(eUserName.vector),
    };
    const dPassphrase = {
        ciphered: fromByteArray(ePassphrase.ciphered),
        vector: fromByteArray(ePassphrase.vector),
    };
    return `${toBase64(JSON.stringify(dUserName))}.${toBase64(JSON.stringify(dPassphrase))}`;
}
export async function decryptSessionToken(token, cryptoKey) {
    if (!token) {
        throw new EmptySessionTokenError();
    }
    const mRaw = token.match(TOKEN_FORMAT);
    if (!mRaw || mRaw.length !== 3) {
        throw new InvalidSessionTokenError();
    }
    const rUserName = fromJson(fromBase64(mRaw[1]));
    const rPassphrase = fromJson(fromBase64(mRaw[2]));
    const dUserName = {
        ciphered: toByteArray(rUserName.ciphered),
        vector: toByteArray(rUserName.vector),
    };
    const dPassphrase = {
        ciphered: toByteArray(rPassphrase.ciphered),
        vector: toByteArray(rPassphrase.vector),
    };
    const [userName, passphrase] = await Promise.all([decrypt(dUserName, cryptoKey), decrypt(dPassphrase, cryptoKey)]);
    return { userName, passphrase };
}
export function decryptSessionHash(hash, salt, cryptoKey) {
    return decrypt({ ciphered: toByteArray(hash), vector: toByteArray(salt) }, cryptoKey);
}
//# sourceMappingURL=crypto.js.map